import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";
import {
  Box, ChevronDown, Download, MapPin, Pencil, Plus, Printer,
  Save, Trash2, Upload, X, Move, ClipboardList, FolderOpen,
} from "lucide-react";

// ─── 3D Viewer Mapping ───────────────────────────────────────────────────────

function get3dViewerUrl(hospitalName: string | null | undefined): string | null {
  if (!hospitalName) return null;
  const n = hospitalName.toLowerCase();
  if (n.includes("euh") || n.includes("emory university hospital")) return "/euh-3d.html";
  if (n.includes("msk") || n.includes("musculoskeletal")) return "/msk-3d.html";
  return null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeployProject {
  id: string;
  hospital_name: string;
  hospital_address: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface DeployCheckpoint {
  id: string;
  project_id: string;
  stop_number: number | null;
  floor: string | null;
  area: string | null;
  location_detail: string | null;
  placement_notes: string | null;
  map_x_pct: number | null;
  map_y_pct: number | null;
  qr_payload: string;
  status: string;
  captured_at: string | null;
  installed_at: string | null;
  verified_at: string | null;
}

interface CheckpointDraft {
  floor: string;
  area: string;
  location_detail: string;
  placement_notes: string;
}

// ─── API Helpers ─────────────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const { token, user } = useAuthStore.getState();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = localStorage.getItem("csrfToken");
  if (csrf) headers["x-csrf-token"] = csrf;
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (user?.email) headers["x-user-email"] = user.email;
  return headers;
}

async function apiGet(action: string, extra = "") {
  const r = await fetch(`/api/deployments?action=${action}${extra}`, {
    headers: getAuthHeaders(),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`API error ${r.status}`);
  return r.json();
}

async function apiPost(action: string, body: unknown) {
  const r = await fetch(`/api/deployments?action=${action}`, {
    method: "POST",
    headers: getAuthHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`API error ${r.status}`);
  return r.json();
}

async function apiDelete(action: string, id: string) {
  const r = await fetch(`/api/deployments?action=${action}&id=${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`API error ${r.status}`);
  return r.json();
}

// ─── Floor Plan Storage (localStorage) ───────────────────────────────────────

function floorPlanKey(projectId: string, floor: string) {
  return `deploy_fp_${projectId}_${floor}`;
}

function saveFloorPlan(projectId: string, floor: string, dataUrl: string) {
  try { localStorage.setItem(floorPlanKey(projectId, floor), dataUrl); } catch { /* storage full */ }
}

function loadFloorPlan(projectId: string, floor: string): string | null {
  return localStorage.getItem(floorPlanKey(projectId, floor));
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function DeploymentPlanner() {
  const { t, lang } = useTranslation();
  // Project list & selection
  const [projects, setProjects] = useState<DeployProject[]>([]);
  const [activeProject, setActiveProject] = useState<DeployProject | null>(null);
  const [checkpoints, setCheckpoints] = useState<DeployCheckpoint[]>([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);

  // New project form
  const [newHospitalName, setNewHospitalName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");

  // Floor plan
  const [floors, setFloors] = useState<string[]>(["1"]);
  const [activeFloor, setActiveFloor] = useState("1");
  const [floorPlanDataUrls, setFloorPlanDataUrls] = useState<Record<string, string>>({});

  // Marker interaction
  const [placingMode, setPlacingMode] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Checkpoint editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CheckpointDraft>({
    floor: "", area: "", location_detail: "", placement_notes: "",
  });

  // Status
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState<Set<string>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Project lifecycle status labels (backend enum → localized text)
  function projectStatusLabel(status: string): string {
    switch (status) {
      case "planning": return t("deploy.statusPlanning", "planning");
      case "capturing": return t("deploy.statusCapturing", "capturing");
      case "fabricating": return t("deploy.statusFabricating", "fabricating");
      case "installing": return t("deploy.statusInstalling", "installing");
      case "complete": return t("deploy.statusComplete", "complete");
      default: return status;
    }
  }

  // ── Load projects ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet("projects");
      setProjects(data.projects || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadProject(id: string) {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet("project", `&id=${id}`);
      setActiveProject(data.project);
      const cks: DeployCheckpoint[] = data.checkpoints || [];
      setCheckpoints(cks);

      // Restore floor plan images from localStorage
      const usedFloors = [...new Set(cks.map(c => c.floor || "1").filter(Boolean))];
      const allFloors = [...new Set(["1", ...usedFloors])];
      setFloors(allFloors);

      const urls: Record<string, string> = {};
      for (const f of allFloors) {
        const stored = loadFloorPlan(id, f);
        if (stored) urls[f] = stored;
      }
      setFloorPlanDataUrls(urls);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Create project ─────────────────────────────────────────────────────────
  async function handleCreateProject(e: React.FormEvent) {
    e.preventDefault();
    if (!newHospitalName.trim()) return;
    setSaving(true);
    try {
      const data = await apiPost("create-project", {
        hospital_name: newHospitalName.trim(),
        hospital_address: newAddress.trim() || null,
        contact_name: newContactName.trim() || null,
        contact_phone: newContactPhone.trim() || null,
      });
      const proj = data.project;
      setProjects(prev => [proj, ...prev]);
      setActiveProject(proj);
      setCheckpoints([]);
      setFloors(["1"]);
      setActiveFloor("1");
      setFloorPlanDataUrls({});
      setShowNewProjectForm(false);
      setNewHospitalName("");
      setNewAddress("");
      setNewContactName("");
      setNewContactPhone("");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Floor plan upload ──────────────────────────────────────────────────────
  function handleFloorPlanFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !activeProject) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      saveFloorPlan(activeProject.id, activeFloor, dataUrl);
      setFloorPlanDataUrls(prev => ({ ...prev, [activeFloor]: dataUrl }));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  // ── Add floor ──────────────────────────────────────────────────────────────
  function addFloor() {
    const next = String(Math.max(...floors.map(Number)) + 1);
    setFloors(prev => [...prev, next]);
    setActiveFloor(next);
  }

  // ── Floor plan click → place marker ────────────────────────────────────────
  const handleFloorPlanClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!placingMode || !activeProject || draggingId) return;
      const rect = containerRef.current!.getBoundingClientRect();
      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;

      const qrPayload = crypto.randomUUID();
      const nextStop = checkpoints.length > 0
        ? Math.max(...checkpoints.map(c => c.stop_number ?? 0)) + 1
        : 1;

      const tempId = `temp-${Date.now()}`;
      const newCk: DeployCheckpoint = {
        id: tempId,
        project_id: activeProject.id,
        stop_number: nextStop,
        floor: activeFloor,
        area: null,
        location_detail: null,
        placement_notes: null,
        map_x_pct: xPct,
        map_y_pct: yPct,
        qr_payload: qrPayload,
        status: "planned",
        captured_at: null,
        installed_at: null,
        verified_at: null,
      };

      setCheckpoints(prev => [...prev, newCk]);
      setEditingId(tempId);
      setDraft({ floor: activeFloor, area: "", location_detail: "", placement_notes: "" });
      setPlacingMode(false);
      setPendingSave(prev => new Set(prev).add(tempId));
    },
    [placingMode, activeProject, draggingId, checkpoints, activeFloor]
  );

  // ── Drag markers ───────────────────────────────────────────────────────────
  const handleMarkerMouseDown = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      e.preventDefault();
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const ck = checkpoints.find(c => c.id === id);
      if (!ck) return;
      const markerX = ((ck.map_x_pct ?? 0) / 100) * rect.width + rect.left;
      const markerY = ((ck.map_y_pct ?? 0) / 100) * rect.height + rect.top;
      setDragOffset({ x: e.clientX - markerX, y: e.clientY - markerY });
      setDraggingId(id);
    },
    [checkpoints]
  );

  const handleContainerMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!draggingId || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const xPct = Math.max(0, Math.min(100, ((e.clientX - dragOffset.x - rect.left) / rect.width) * 100));
      const yPct = Math.max(0, Math.min(100, ((e.clientY - dragOffset.y - rect.top) / rect.height) * 100));
      setCheckpoints(prev =>
        prev.map(c => c.id === draggingId ? { ...c, map_x_pct: xPct, map_y_pct: yPct } : c)
      );
    },
    [draggingId, dragOffset]
  );

  const handleContainerMouseUp = useCallback(() => {
    if (draggingId) {
      setPendingSave(prev => new Set(prev).add(draggingId));
      setDraggingId(null);
    }
  }, [draggingId]);

  // ── Edit checkpoint ────────────────────────────────────────────────────────
  function startEdit(ck: DeployCheckpoint) {
    setEditingId(ck.id);
    setDraft({
      floor: ck.floor || activeFloor,
      area: ck.area || "",
      location_detail: ck.location_detail || "",
      placement_notes: ck.placement_notes || "",
    });
  }

  async function saveEdit() {
    if (!editingId || !activeProject) return;
    setSaving(true);
    try {
      const ck = checkpoints.find(c => c.id === editingId)!;
      const isTemp = editingId.startsWith("temp-");

      if (isTemp) {
        const data = await apiPost("create-checkpoint", {
          project_id: activeProject.id,
          floor: draft.floor || activeFloor,
          area: draft.area || null,
          location_detail: draft.location_detail || null,
          placement_notes: draft.placement_notes || null,
          map_x_pct: ck.map_x_pct,
          map_y_pct: ck.map_y_pct,
          stop_number: ck.stop_number,
          qr_payload: ck.qr_payload,
        });
        const saved = data.checkpoint;
        setCheckpoints(prev => prev.map(c => c.id === editingId ? { ...c, ...saved } : c));
      } else {
        const data = await apiPost("update-checkpoint", {
          id: editingId,
          floor: draft.floor || null,
          area: draft.area || null,
          location_detail: draft.location_detail || null,
          placement_notes: draft.placement_notes || null,
        });
        setCheckpoints(prev => prev.map(c => c.id === editingId ? { ...c, ...data.checkpoint } : c));
      }
      setPendingSave(prev => { const s = new Set(prev); s.delete(editingId); return s; });
      setEditingId(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    if (!editingId) return;
    if (editingId.startsWith("temp-")) {
      setCheckpoints(prev => prev.filter(c => c.id !== editingId));
    }
    setPendingSave(prev => { const s = new Set(prev); s.delete(editingId); return s; });
    setEditingId(null);
  }

  // ── Save position after drag ───────────────────────────────────────────────
  useEffect(() => {
    if (pendingSave.size === 0) return;
    const ids = [...pendingSave].filter(id => !id.startsWith("temp-") && id !== editingId);
    if (ids.length === 0) return;

    const timer = setTimeout(async () => {
      for (const id of ids) {
        const ck = checkpoints.find(c => c.id === id);
        if (!ck) continue;
        try {
          await apiPost("update-checkpoint", {
            id,
            map_x_pct: ck.map_x_pct,
            map_y_pct: ck.map_y_pct,
          });
          setPendingSave(prev => { const s = new Set(prev); s.delete(id); return s; });
        } catch { /* will retry */ }
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [pendingSave, checkpoints, editingId]);

  // ── Delete checkpoint ──────────────────────────────────────────────────────
  async function deleteCheckpoint(id: string) {
    if (id.startsWith("temp-")) {
      setCheckpoints(prev => prev.filter(c => c.id !== id));
      return;
    }
    try {
      await apiDelete("delete-checkpoint", id);
      setCheckpoints(prev => prev.filter(c => c.id !== id));
    } catch (e: any) {
      setError(e.message);
    }
  }

  // ── Print checklist ────────────────────────────────────────────────────────
  function printChecklist() {
    window.print();
  }

  // ── Checkpoints for current floor ─────────────────────────────────────────
  const floorCheckpoints = checkpoints.filter(c => (c.floor || "1") === activeFloor);
  const allSorted = [...checkpoints].sort((a, b) => (a.stop_number ?? 999) - (b.stop_number ?? 999));

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-gray-50 print:bg-white">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 print:hidden">
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-900">{t("deploy.title", "Deployment Planner")}</h1>
          {activeProject && (
            <span className="text-sm text-gray-500">— {activeProject.hospital_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeProject && (
            <>
              <span className="text-xs text-gray-400">
                {pendingSave.size > 0 ? t("deploy.unsavedChanges", "Unsaved changes...") : t("deploy.allSaved", "All saved")}
              </span>
              <button
                onClick={printChecklist}
                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
              >
                <Printer className="w-4 h-4" /> {t("deploy.printChecklist", "Print Checklist")}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 print:hidden">
          {error}{" "}
          <button onClick={() => setError(null)} className="ml-2 underline">{t("deploy.dismiss", "dismiss")}</button>
        </div>
      )}

      {/* ── Project Selector Bar ── */}
      <div className="px-6 py-3 bg-white border-b border-gray-100 flex items-center gap-3 flex-wrap print:hidden">
        <div className="relative">
          <button
            onClick={() => setShowProjectDropdown(!showProjectDropdown)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 min-w-[220px]"
          >
            <FolderOpen className="w-4 h-4 text-gray-500" />
            <span className="flex-1 text-left truncate">
              {activeProject ? activeProject.hospital_name : t("deploy.selectProject", "Select a project…")}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          {showProjectDropdown && (
            <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-sm text-gray-500">{t("common.loading", "Loading…")}</div>
              ) : projects.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">{t("deploy.noProjectsYet", "No projects yet.")}</div>
              ) : (
                projects.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { loadProject(p.id); setShowProjectDropdown(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0"
                  >
                    <div className="font-medium text-gray-800">{p.hospital_name}</div>
                    <div className="text-xs text-gray-400">{projectStatusLabel(p.status)} · {new Intl.DateTimeFormat(lang, { year: "numeric", month: "short", day: "numeric" }).format(new Date(p.created_at))}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => { setShowNewProjectForm(!showNewProjectForm); setShowProjectDropdown(false); }}
          className="flex items-center gap-1 px-3 py-2 text-sm rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
        >
          <Plus className="w-4 h-4" /> {t("deploy.newProject", "New Project")}
        </button>

        {activeProject && (
          <select
            value={activeProject.status}
            onChange={async (e) => {
              try {
                await apiPost("update-status", { id: activeProject.id, status: e.target.value });
                setActiveProject(p => p ? { ...p, status: e.target.value } : p);
                setProjects(ps => ps.map(p => p.id === activeProject.id ? { ...p, status: e.target.value } : p));
              } catch { /* ignore */ }
            }}
            className="text-sm px-2 py-2 rounded border border-gray-300 bg-white"
          >
            {["planning", "capturing", "fabricating", "installing", "complete"].map(s => (
              <option key={s} value={s}>{projectStatusLabel(s)}</option>
            ))}
          </select>
        )}

        {activeProject && get3dViewerUrl(activeProject.hospital_name) && (
          <button
            type="button"
            onClick={() => {
              const url = get3dViewerUrl(activeProject.hospital_name);
              if (url) window.open(url, "_blank");
            }}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 transition-all shadow-sm"
          >
            <Box className="w-4 h-4" />
            {t("deploy.simulation3d", "3D Simulation")}
          </button>
        )}
      </div>

      {/* ── New Project Form ── */}
      {showNewProjectForm && (
        <div className="mx-6 mt-4 p-5 bg-white border border-gray-200 rounded-lg shadow-sm print:hidden">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">{t("deploy.newDeploymentProject", "New Deployment Project")}</h2>
          <form onSubmit={handleCreateProject} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("deploy.hospitalNameRequired", "Hospital Name *")}</label>
              <input
                value={newHospitalName}
                onChange={e => setNewHospitalName(e.target.value)}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                placeholder="Emory University Hospital"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("deploy.address", "Address")}</label>
              <input
                value={newAddress}
                onChange={e => setNewAddress(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                placeholder="1364 Clifton Rd NE, Atlanta, GA 30322"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("deploy.siteContactName", "Site Contact Name")}</label>
              <input
                value={newContactName}
                onChange={e => setNewContactName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("deploy.contactPhone", "Contact Phone")}</label>
              <input
                value={newContactPhone}
                onChange={e => setNewContactPhone(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                placeholder="(404) 555-0100"
              />
            </div>
            <div className="col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? t("deploy.creating", "Creating…") : t("deploy.createProject", "Create Project")}
              </button>
              <button
                type="button"
                onClick={() => setShowNewProjectForm(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                {t("common.cancel", "Cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Main Content ── */}
      {activeProject ? (
        <div className="flex flex-1 min-h-0 gap-0">
          {/* Left Panel: Checkpoint List */}
          <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden print:w-full print:border-0">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between print:hidden">
              <span className="text-sm font-semibold text-gray-700">
                {t("deploy.checkpointsCount", "Checkpoints ({n})").replace("{n}", String(allSorted.length))}
              </span>
              <button
                onClick={() => { setPlacingMode(!placingMode); setEditingId(null); }}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${
                  placingMode
                    ? "bg-blue-600 text-white"
                    : "border border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <MapPin className="w-3 h-3" />
                {placingMode ? t("deploy.clickFloorPlan", "Click floor plan…") : t("deploy.placeMarker", "Place Marker")}
              </button>
            </div>

            {/* Printable header */}
            <div className="hidden print:block px-6 py-4 border-b">
              <h2 className="text-xl font-bold">{activeProject.hospital_name}</h2>
              <p className="text-sm text-gray-600">{activeProject.hospital_address}</p>
              <p className="text-sm text-gray-600">{t("deploy.contactLabel", "Contact:")} {activeProject.contact_name} {activeProject.contact_phone}</p>
              <p className="text-sm text-gray-500 mt-1">{t("deploy.walkthroughChecklistStops", "Deployment Walkthrough Checklist — {n} stops").replace("{n}", String(allSorted.length))}</p>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {allSorted.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>{t("deploy.noCheckpointsYet", "No checkpoints yet.")}</p>
                  <p className="mt-1">{t("deploy.noCheckpointsHint", "Click \"Place Marker\" then click on the floor plan.")}</p>
                </div>
              ) : (
                allSorted.map((ck, idx) => (
                  <CheckpointListItem
                    key={ck.id}
                    ck={ck}
                    t={t}
                    idx={idx}
                    isEditing={editingId === ck.id}
                    draft={draft}
                    setDraft={setDraft}
                    onEdit={() => startEdit(ck)}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    onDelete={() => deleteCheckpoint(ck.id)}
                    saving={saving}
                    onFloorClick={() => {
                      if (ck.floor && ck.floor !== activeFloor) setActiveFloor(ck.floor);
                    }}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right Panel: Floor Plan */}
          <div className="flex-1 flex flex-col min-w-0 print:hidden">
            {/* Floor tabs */}
            <div className="flex items-center gap-1 px-4 py-2 bg-white border-b border-gray-200">
              <span className="text-xs text-gray-500 mr-2">{t("deploy.floorLabel", "Floor:")}</span>
              {floors.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFloor(f)}
                  className={`px-3 py-1 text-xs rounded-md font-medium ${
                    activeFloor === f
                      ? "bg-blue-600 text-white"
                      : "border border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {f}
                  {floorCheckpoints.filter(c => (c.floor || "1") === f).length > 0 && (
                    <span className="ml-1 opacity-70">
                      ({checkpoints.filter(c => (c.floor || "1") === f).length})
                    </span>
                  )}
                </button>
              ))}
              <button
                onClick={addFloor}
                className="px-2 py-1 text-xs rounded border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50"
              >
                {t("deploy.addFloor", "+ Floor")}
              </button>

              <div className="ml-auto flex items-center gap-2">
                {placingMode && (
                  <span className="text-xs font-medium text-blue-600 animate-pulse">
                    {t("deploy.clickAnywhereToPlace", "Click anywhere to place marker")}
                  </span>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFloorPlanFileChange}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                >
                  <Upload className="w-3 h-3" />
                  {t("deploy.uploadFloorPlan", "Upload Floor Plan")}
                </button>
              </div>
            </div>

            {/* Floor plan canvas */}
            <div className="flex-1 flex items-start justify-center p-4 bg-gray-100 overflow-auto">
              <div
                ref={containerRef}
                className={`relative bg-white shadow rounded select-none ${
                  placingMode ? "cursor-crosshair" : draggingId ? "cursor-grabbing" : "cursor-default"
                }`}
                style={{ width: "100%", maxWidth: 900, aspectRatio: "4/3" }}
                onClick={handleFloorPlanClick}
                onMouseMove={handleContainerMouseMove}
                onMouseUp={handleContainerMouseUp}
                onMouseLeave={handleContainerMouseUp}
              >
                {floorPlanDataUrls[activeFloor] ? (
                  <img
                    src={floorPlanDataUrls[activeFloor]}
                    alt={t("deploy.floorPlanAlt", "Floor {n} plan").replace("{n}", activeFloor)}
                    className="w-full h-full object-contain pointer-events-none rounded"
                    draggable={false}
                  />
                ) : (
                  <div
                    className="w-full h-full flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-300 rounded"
                    onClick={() => !placingMode && fileInputRef.current?.click()}
                  >
                    <Upload className="w-10 h-10 mb-2 opacity-40" />
                    <p className="text-sm">{t("deploy.uploadFloorPlanFor", "Upload a floor plan for Floor {n}").replace("{n}", activeFloor)}</p>
                    <p className="text-xs mt-1 opacity-60">{t("deploy.fileFormats", "PNG, JPG, or PDF")}</p>
                  </div>
                )}

                {/* Checkpoint markers */}
                {checkpoints
                  .filter(c => (c.floor || "1") === activeFloor && c.map_x_pct != null)
                  .map((ck, idx) => {
                    const globalIdx = allSorted.findIndex(c => c.id === ck.id);
                    return (
                      <CheckpointMarker
                        key={ck.id}
                        ck={ck}
                        t={t}
                        label={String((ck.stop_number ?? globalIdx + 1))}
                        isEditing={editingId === ck.id}
                        isDragging={draggingId === ck.id}
                        onMouseDown={(e) => handleMarkerMouseDown(e, ck.id)}
                        onClick={(e) => { e.stopPropagation(); startEdit(ck); }}
                      />
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        !showNewProjectForm && (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-base font-medium">{t("deploy.noProjectSelected", "No project selected")}</p>
              <p className="text-sm mt-1">{t("deploy.noProjectSelectedHint", "Select an existing project or create a new one above.")}</p>
            </div>
          </div>
        )
      )}
    </div>
  );
}

// ─── Checkpoint Marker (on floor plan) ───────────────────────────────────────

function CheckpointMarker({
  ck, t, label, isEditing, isDragging, onMouseDown, onClick,
}: {
  ck: DeployCheckpoint;
  t: (key: string, fallback?: string) => string;
  label: string;
  isEditing: boolean;
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}) {
  const statusColor =
    ck.status === "verified" ? "bg-green-500" :
    ck.status === "installed" ? "bg-blue-500" :
    ck.status === "captured" ? "bg-yellow-500" :
    isEditing ? "bg-orange-500" :
    "bg-red-500";

  return (
    <div
      className={`absolute z-10 flex items-center justify-center rounded-full text-white text-xs font-bold shadow-lg
        ${statusColor} ${isDragging ? "opacity-80 scale-110" : "hover:scale-110"}
        transition-transform cursor-grab active:cursor-grabbing`}
      style={{
        left: `${ck.map_x_pct}%`,
        top: `${ck.map_y_pct}%`,
        width: 28,
        height: 28,
        transform: "translate(-50%, -50%)",
      }}
      onMouseDown={onMouseDown}
      onClick={onClick}
      title={`${t("deploy.stop", "Stop")} ${label}: ${ck.area || t("deploy.unnamedLower", "unnamed")}`}
    >
      {label}
    </div>
  );
}

// ─── Checkpoint status label (backend enum → localized) ──────────────────────

function checkpointStatusLabel(status: string, t: (key: string, fallback?: string) => string): string {
  switch (status) {
    case "planned": return t("deploy.ckStatusPlanned", "planned");
    case "captured": return t("deploy.ckStatusCaptured", "captured");
    case "installed": return t("deploy.ckStatusInstalled", "installed");
    case "verified": return t("deploy.ckStatusVerified", "verified");
    default: return status;
  }
}

// ─── Checkpoint List Item ─────────────────────────────────────────────────────

function CheckpointListItem({
  ck, t, idx, isEditing, draft, setDraft, onEdit, onSave, onCancel, onDelete, saving, onFloorClick,
}: {
  ck: DeployCheckpoint;
  t: (key: string, fallback?: string) => string;
  idx: number;
  isEditing: boolean;
  draft: CheckpointDraft;
  setDraft: (d: CheckpointDraft) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  saving: boolean;
  onFloorClick: () => void;
}) {
  const statusBadge =
    ck.status === "verified" ? "bg-green-100 text-green-800" :
    ck.status === "installed" ? "bg-blue-100 text-blue-800" :
    ck.status === "captured" ? "bg-yellow-100 text-yellow-800" :
    "bg-gray-100 text-gray-500";

  return (
    <div className={`px-3 py-2.5 border-b border-gray-50 print:border print:border-gray-200 print:m-2 print:rounded ${isEditing ? "bg-blue-50" : "hover:bg-gray-50"}`}>
      {/* List view */}
      {!isEditing ? (
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-700 text-white text-xs flex items-center justify-center font-bold print:border print:border-gray-700 print:text-gray-800 print:bg-transparent">
            {ck.stop_number ?? idx + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-800 truncate">
                {ck.area || <span className="text-gray-400 italic">{t("deploy.unnamed", "Unnamed")}</span>}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${statusBadge} print:hidden`}>
                {checkpointStatusLabel(ck.status, t)}
              </span>
            </div>
            <button onClick={onFloorClick} className="text-xs text-gray-500">
              {t("deploy.floorN", "Floor {n}").replace("{n}", ck.floor ?? "?")}{ck.location_detail ? ` · ${ck.location_detail}` : ""}
            </button>
            {ck.placement_notes && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">{ck.placement_notes}</p>
            )}
            <div className="mt-1">
              <p className="text-[10px] font-mono text-gray-300 truncate print:text-gray-600 print:font-normal print:text-xs">
                {t("deploy.qrPrefix", "QR:")} {ck.qr_payload}
              </p>
            </div>
            {/* Print checklist fields */}
            <div className="hidden print:block mt-2 space-y-1">
              <div className="flex gap-4 text-xs text-gray-600">
                <span>{t("deploy.areaLabel", "Area:")} {ck.area || "_______________"}</span>
                <span>{t("deploy.floorColon", "Floor:")} {ck.floor || "___"}</span>
              </div>
              <div className="text-xs text-gray-600">{t("deploy.locationLabel", "Location:")} {ck.location_detail || "________________________________"}</div>
              <div className="text-xs text-gray-600">{t("deploy.notesLabel", "Notes:")} {ck.placement_notes || "________________________________"}</div>
              <div className="flex gap-8 text-xs mt-2 text-gray-500">
                <span>☐ {t("deploy.qrPlaced", "QR placed")}</span>
                <span>☐ {t("deploy.scanVerified", "Scan verified")}</span>
                <span>{t("deploy.initials", "Initials: ______")}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-1 print:hidden">
            <button onClick={onEdit} className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete} className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        /* Edit form */
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">
              {ck.stop_number ?? idx + 1}
            </div>
            <span className="text-xs font-semibold text-orange-700">{t("deploy.editingCheckpoint", "Editing checkpoint")}</span>
          </div>
          <div>
            <label className="text-xs text-gray-500">{t("deploy.floor", "Floor")}</label>
            <input
              value={draft.floor}
              onChange={e => setDraft({ ...draft, floor: e.target.value })}
              className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded"
              placeholder="1"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">{t("deploy.areaSuite", "Area / Suite")}</label>
            <input
              value={draft.area}
              onChange={e => setDraft({ ...draft, area: e.target.value })}
              className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded"
              placeholder={t("deploy.areaPlaceholder", "ER Entrance, Admin Suite 200…")}
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">{t("deploy.locationDetail", "Location Detail")}</label>
            <input
              value={draft.location_detail}
              onChange={e => setDraft({ ...draft, location_detail: e.target.value })}
              className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded"
              placeholder={t("deploy.locationDetailPlaceholder", "Left of main doors, above hand sanitizer…")}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">{t("deploy.placementNotes", "Placement Notes")}</label>
            <textarea
              value={draft.placement_notes}
              onChange={e => setDraft({ ...draft, placement_notes: e.target.value })}
              className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded resize-none"
              rows={2}
              placeholder={t("deploy.placementNotesPlaceholder", "5ft high, brick wall, permanent adhesive…")}
            />
          </div>
          <div className="text-[10px] text-gray-400 font-mono break-all">
            {t("deploy.uuidPrefix", "UUID:")} {ck.qr_payload}
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" /> {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
            </button>
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
            >
              {t("common.cancel", "Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
